import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import Select from "react-select";
import useApi from "../../../../hooks/useApi";
import {
  getCompanyUsers,
  closeCompanyManagerAccount,
} from "../../../../services/userService";
import { deleteCompany } from "../../../../services/companyService";
import { useAuth } from "../../../../hooks/useAuth";
import Alert from "../../../utils/Alert";
import Button from "../../../buttons/Button";
import DropdownIndicator from "../../../inputs/DropdownIndicator";
import { stopControllingCompany } from "../../../../redux/slices/adminSlice";
import useLangNavigate from "../../../../hooks/useLangNavigate";

const DangerZone = ({
  tKey = "dashboard.settings.dangerZone.",
  managerUserId = null,
}) => {
  const { t } = useTranslation();
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [hasToConfirmCloseCompany, setHasToConfirmCloseCompany] = useState(false);
  const [hasToConfirmCloseAccount, setHasToConfirmCloseAccount] =
    useState(false);
  const { logout, isOffoodAdmin } = useAuth();
  const dispatch = useDispatch();
  const navigate = useLangNavigate();
  const { request: getUsersRequest } = useApi(getCompanyUsers);
  const { request: deleteCompanyRequest } = useApi(deleteCompany);
  const { request: closeCompanyManagerAccountRequest } = useApi(
    closeCompanyManagerAccount
  );
  const user = useSelector((state) => state.user);
  const controlledCompanyId = useSelector(
    (state) => state.admin.controlledCompanyId
  );
  const companyId = controlledCompanyId || user.companyId;
  const effectiveManagerUserId = managerUserId ?? user.userId;

  const exitAfterDangerAction = useCallback(() => {
    if (!isOffoodAdmin) {
      logout();
    } else {
      dispatch(stopControllingCompany());
      navigate("/dashboard");
    }
  }, [isOffoodAdmin, logout, dispatch, navigate]);

  useEffect(() => {
    if (!companyId) return;
    getUsersRequest(companyId)
      .then((response) => {
        setCompanyUsers(response.data.users || []);
      })
      .catch((error) => {
        console.log(error);
        setCompanyUsers([]);
      });
  }, [companyId, getUsersRequest]);

  const otherMembers = useMemo(
    () =>
      companyUsers.filter(
        (member) => member.userId !== effectiveManagerUserId
      ),
    [companyUsers, effectiveManagerUserId]
  );

  const validatedSuccessors = useMemo(
    () =>
      otherMembers.filter((member) => member.usercompany?.isConfirmed),
    [otherMembers]
  );

  const isManagerAlone = otherMembers.length === 0;
  const hasUnvalidatedMembersOnly =
    otherMembers.length > 0 && validatedSuccessors.length === 0;

  const handleCloseCompany = useCallback(async () => {
    await deleteCompanyRequest(companyId)
      .then(() => {
        exitAfterDangerAction();
        toast.success(t(`${tKey}companyDeletion.success`));
      })
      .catch((error) => {
        toast.error(t(`${tKey}companyDeletion.error`));
        console.log(error);
      });
  }, [
    companyId,
    deleteCompanyRequest,
    exitAfterDangerAction,
    t,
    tKey,
  ]);

  const handleCloseAccount = useCallback(async () => {
    const payload =
      !isManagerAlone && selectedEmployee
        ? { newManagerId: selectedEmployee.userId }
        : {};

    await closeCompanyManagerAccountRequest(companyId, payload)
      .then((response) => {
        exitAfterDangerAction();
        const companyClosed = response?.data?.companyClosed;
        toast.success(
          t(
            companyClosed
              ? `${tKey}accountDeletion.soloSuccess`
              : `${tKey}accountDeletion.success`
          )
        );
      })
      .catch((error) => {
        toast.error(t(`${tKey}accountDeletion.error`));
        console.log(error);
      });
  }, [
    companyId,
    closeCompanyManagerAccountRequest,
    isManagerAlone,
    selectedEmployee,
    exitAfterDangerAction,
    t,
    tKey,
  ]);

  const canConfirmCloseAccount =
    isManagerAlone ||
    (validatedSuccessors.length > 0 && selectedEmployee !== null);

  return (
    <div className="dashboard-content flex column gap-2">
      <div>
        <Alert variant="danger">
          <h4 className="my-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {t(`${tKey}companyDeletion.alert.title`)}
          </h4>
          <p>{t(`${tKey}companyDeletion.alert.text`)}</p>
          <Button
            variant={hasToConfirmCloseCompany ? "secondary" : "danger"}
            size="sm"
            className="mt-2"
            onClick={() =>
              setHasToConfirmCloseCompany(!hasToConfirmCloseCompany)
            }
          >
            {t(
              hasToConfirmCloseCompany
                ? `utils.common.cancel`
                : `${tKey}companyDeletion.button`
            )}
          </Button>
          {hasToConfirmCloseCompany && (
            <Button
              variant="danger"
              size="sm"
              className="mt-2"
              onClick={handleCloseCompany}
            >
              {t(`${tKey}companyDeletion.lastButton`)}
            </Button>
          )}
        </Alert>
      </div>

      <div>
        <Alert variant="danger">
          <h4 className="my-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {t(`${tKey}accountDeletion.alert.title`)}
          </h4>
          <p>
            {t(
              isManagerAlone
                ? `${tKey}accountDeletion.solo.text`
                : `${tKey}accountDeletion.alert.text`
            )}
          </p>
          {hasUnvalidatedMembersOnly && (
            <p className="mt-2">{t(`${tKey}accountDeletion.info`)}</p>
          )}
          <div className="flex align-center gap-1">
            <Button
              variant={hasToConfirmCloseAccount ? "secondary" : "danger"}
              size="sm"
              className="mt-2"
              onClick={() =>
                setHasToConfirmCloseAccount(!hasToConfirmCloseAccount)
              }
            >
              {t(
                hasToConfirmCloseAccount
                  ? `utils.common.cancel`
                  : `${tKey}accountDeletion.button`
              )}
            </Button>
            {hasToConfirmCloseAccount && validatedSuccessors.length > 0 && (
              <Select
                className="react-custom-select text-dark col-3"
                classNamePrefix="react-select"
                placeholder={t(`${tKey}accountDeletion.placeholder`)}
                options={validatedSuccessors.map((employee) => ({
                  value: employee,
                  label: `${employee.firstname} ${employee.lastname}`,
                }))}
                onChange={(selected) => setSelectedEmployee(selected.value)}
                components={{ DropdownIndicator }}
              />
            )}
          </div>
          {hasToConfirmCloseAccount && (
            <Button
              variant="danger"
              size="sm"
              className="mt-2"
              disabled={!canConfirmCloseAccount}
              onClick={handleCloseAccount}
            >
              {t(
                isManagerAlone
                  ? `${tKey}accountDeletion.solo.confirmButton`
                  : `${tKey}accountDeletion.lastButton`
              )}
            </Button>
          )}
        </Alert>
      </div>
    </div>
  );
};

export default DangerZone;
